import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
	TErrorCorrectionsResponsePayload,
	TErrorCorrectionsStore,
	TErrorCorrectionsRequest,
	TErrorCorrectionRequest,
	TErrorCorrectionResponsePayload,
	TErrorCorrectionsErrorTypesResponsePayload,
	TErrorCorrectionsScriptHistoryRequest,
	TErrorCorrectionsScriptHistoryResponsePayload,
	TErrorCorrectionsUpdateCommentRequest,
	TErrorCorrectionsUpdateCommunicationWithClientRequest,
	TErrorCorrectionsUpdateProposedSolutionRequest,
	TErrorCorrectionsUpdateResponsibleUserRequest,
	TErrorCorrectionsUpdateStatusRequest,
	TErrorCorrectionsScriptsResponsePayload,
	TErrorCorrectionsStartScriptRequest,
	TErrorCorrectionsStartScriptResponsePayload,
	TErrorCorrectionsUserStatisticResponsePayload,
	TLaunchedScriptsRequest,
	TLaunchedScriptsResponsePayload,
	TStartScriptByOrderRequest,
} from './types';
import { getUser } from '../user/selectors';

export const initialState: TErrorCorrectionsStore = {
	errorCorrections: null,
	errorCorrectionsLoader: false,
	lastListRequest: null,
	errorCorrection: null,
	errorCorrectionLoader: false,
	errorTypes: null,
	errorTypesLoader: false,
	scriptsHistory: null,
	scriptsHistoryLoader: false,
	scripts: null,
	scriptsLoader: false,
	startScriptLoader: false,
	startScriptError: null,
	parameterUpdateCounter: 0,
	startScriptResponse: null,
	startScriptResponseArray: [],
	userStatistic: null,
	userStatisticLoader: false,
	launchedScripts: null,
	launchedScriptsLoader: false,
};

// ==========================================:
const errorCorrectionsReducer = createSlice({
	name: '@@errorCorrections',
	initialState,
	reducers: {
		getErrorCorrectionsRequest: (
			state: TErrorCorrectionsStore,
			action: PayloadAction<TErrorCorrectionsRequest>,
		) => {
			const errorCorrectionsState = state;
			errorCorrectionsState.errorCorrectionsLoader = true;
			errorCorrectionsState.lastListRequest = action.payload;
		},
		getErrorCorrectionsSuccess: (
			state: TErrorCorrectionsStore,
			action: PayloadAction<TErrorCorrectionsResponsePayload>,
		) => {
			const { payload } = action;
			const errorCorrectionsState = state;
			errorCorrectionsState.errorCorrections = payload;
			errorCorrectionsState.errorCorrectionsLoader = false;
		},
		getErrorCorrectionRequest: (
			state: TErrorCorrectionsStore,
			action: PayloadAction<TErrorCorrectionRequest>,
		) => {
			const errorCorrectionsState = state;
			errorCorrectionsState.errorCorrectionLoader = true;
		},
		getErrorCorrectionSuccess: (
			state: TErrorCorrectionsStore,
			action: PayloadAction<TErrorCorrectionResponsePayload>,
		) => {
			const { payload } = action;
			const errorCorrectionsState = state;
			errorCorrectionsState.errorCorrection = payload;
			errorCorrectionsState.errorCorrectionLoader = false;
		},
		getErrorTypesRequest: (state: TErrorCorrectionsStore) => {
			const errorCorrectionsState = state;
			errorCorrectionsState.errorTypesLoader = true;
		},
		getErrorTypesSuccess: (
			state: TErrorCorrectionsStore,
			action: PayloadAction<TErrorCorrectionsErrorTypesResponsePayload>,
		) => {
			const { payload } = action;
			const errorCorrectionsState = state;
			errorCorrectionsState.errorTypes = payload;
			errorCorrectionsState.errorTypesLoader = false;
		},
		getErrorCorrectionsScriptHistoryRequest: (
			state: TErrorCorrectionsStore,
			action: PayloadAction<TErrorCorrectionsScriptHistoryRequest>,
		) => {
			const errorCorrectionsState = state;
			errorCorrectionsState.scriptsHistoryLoader = true;
		},
		getErrorCorrectionsScriptHistorySuccess: (
			state: TErrorCorrectionsStore,
			action: PayloadAction<TErrorCorrectionsScriptHistoryResponsePayload>,
		) => {
			const { payload } = action;
			const errorCorrectionsState = state;
			errorCorrectionsState.scriptsHistory = payload;
			errorCorrectionsState.scriptsHistoryLoader = false;
		},
		updateCommentRequest: (
			state: TErrorCorrectionsStore,
			action: PayloadAction<TErrorCorrectionsUpdateCommentRequest>,
		) => {},
		updateCommunicationWithClientRequest: (
			state: TErrorCorrectionsStore,
			action: PayloadAction<TErrorCorrectionsUpdateCommunicationWithClientRequest>,
		) => {},
		updateProposedSolutionRequest: (
			state: TErrorCorrectionsStore,
			action: PayloadAction<TErrorCorrectionsUpdateProposedSolutionRequest>,
		) => {},
		updateResponsibleUserRequest: (
			state: TErrorCorrectionsStore,
			action: PayloadAction<TErrorCorrectionsUpdateResponsibleUserRequest>,
		) => {},
		updateStatusRequest: (
			state: TErrorCorrectionsStore,
			action: PayloadAction<TErrorCorrectionsUpdateStatusRequest>,
		) => {},
		getScriptsRequest: (state: TErrorCorrectionsStore) => {
			const errorCorrectionsState = state;
			errorCorrectionsState.scriptsLoader = true;
		},
		getScriptsSuccess: (
			state: TErrorCorrectionsStore,
			action: PayloadAction<TErrorCorrectionsScriptsResponsePayload>,
		) => {
			const { payload } = action;
			const errorCorrectionsState = state;
			errorCorrectionsState.scripts = payload;
			errorCorrectionsState.scriptsLoader = false;
		},
		startScriptRequest: (
			state: TErrorCorrectionsStore,
			action: PayloadAction<TErrorCorrectionsStartScriptRequest>,
		) => {
			const errorCorrectionsState = state;
			errorCorrectionsState.startScriptLoader = true;
			errorCorrectionsState.startScriptError = null;
		},
		startScriptSuccess: (
			state: TErrorCorrectionsStore,
			action: PayloadAction<TErrorCorrectionsStartScriptResponsePayload>,
		) => {
			const { payload } = action;
			const errorCorrectionsState = state;
			errorCorrectionsState.startScriptLoader = false;
			errorCorrectionsState.startScriptError = null;
			errorCorrectionsState.startScriptResponse = payload;
			errorCorrectionsState.startScriptResponseArray.push(payload);
		},
		startScriptResponseArrayReset: (state: TErrorCorrectionsStore) => {
			const errorCorrectionsState = state;
			errorCorrectionsState.startScriptResponseArray = [];
		},
		startScriptFailure: (state: TErrorCorrectionsStore, action: PayloadAction<string>) => {
			const errorCorrectionsState = state;
			errorCorrectionsState.startScriptLoader = false;
			errorCorrectionsState.startScriptError = action.payload;
		},
		resetParameterUpdateCounter: (state: TErrorCorrectionsStore) => {
			const errorCorrectionsState = state;
			errorCorrectionsState.parameterUpdateCounter = 0;
		},
		updateParameterUpdateCounter: (state: TErrorCorrectionsStore) => {
			const errorCorrectionsState = state;
			errorCorrectionsState.parameterUpdateCounter += 1;
		},
		getUserStatisticRequest: (state: TErrorCorrectionsStore) => {
			const errorCorrectionsState = state;
			errorCorrectionsState.userStatisticLoader = true;
		},
		getUserStatisticSuccess: (
			state: TErrorCorrectionsStore,
			action: PayloadAction<TErrorCorrectionsUserStatisticResponsePayload>,
		) => {
			const { payload } = action;
			const errorCorrectionsState = state;
			errorCorrectionsState.userStatistic = payload;
			errorCorrectionsState.userStatisticLoader = false;
		},

		getLaunchedScriptsRequest: (
			state: TErrorCorrectionsStore,
			action: PayloadAction<TLaunchedScriptsRequest>,
		) => {
			const errorCorrectionsState = state;
			errorCorrectionsState.launchedScriptsLoader = true;
		},
		getLaunchedScriptsSuccess: (
			state: TErrorCorrectionsStore,
			action: PayloadAction<TLaunchedScriptsResponsePayload>,
		) => {
			const { payload } = action;
			const errorCorrectionsState = state;
			errorCorrectionsState.launchedScripts = payload;
			errorCorrectionsState.launchedScriptsLoader = false;
		},
		startScriptByOrderRequest: (
			state: TErrorCorrectionsStore,
			action: PayloadAction<TStartScriptByOrderRequest>,
		) => {
			const errorCorrectionsState = state;
			errorCorrectionsState.startScriptLoader = true;
			errorCorrectionsState.startScriptError = null;
		},
		startScriptByOrderSuccess: (
			state: TErrorCorrectionsStore,
			action: PayloadAction<TErrorCorrectionsStartScriptResponsePayload>,
		) => {
			const { payload } = action;
			const errorCorrectionsState = state;
			errorCorrectionsState.startScriptLoader = false;
			errorCorrectionsState.startScriptError = null;
		},
		startScriptByOrderFailure: (state: TErrorCorrectionsStore, action: PayloadAction<string>) => {
			const errorCorrectionsState = state;
			errorCorrectionsState.startScriptLoader = false;
			errorCorrectionsState.startScriptError = action.payload;
		},

		assetsInitState: () => initialState,
	},
});

export default errorCorrectionsReducer.reducer;
export const {
	assetsInitState,
	getErrorCorrectionsRequest,
	getErrorCorrectionsSuccess,
	getErrorCorrectionRequest,
	getErrorCorrectionSuccess,
	getErrorTypesRequest,
	getErrorTypesSuccess,
	getErrorCorrectionsScriptHistoryRequest,
	getErrorCorrectionsScriptHistorySuccess,
	updateCommentRequest,
	updateCommunicationWithClientRequest,
	updateProposedSolutionRequest,
	updateResponsibleUserRequest,
	updateStatusRequest,
	getScriptsRequest,
	getScriptsSuccess,
	startScriptRequest,
	startScriptSuccess,
	startScriptFailure,
	resetParameterUpdateCounter,
	updateParameterUpdateCounter,
	startScriptResponseArrayReset,
	getUserStatisticRequest,
	getUserStatisticSuccess,
	getLaunchedScriptsRequest,
	getLaunchedScriptsSuccess,
	startScriptByOrderRequest,
	startScriptByOrderSuccess,
	startScriptByOrderFailure,
} = errorCorrectionsReducer.actions;
